import { useCallback, useState } from "react";

import {
  ObservabilityService,
  type LatencyPercentiles,
  type ObservabilityWindow,
} from "@/api";
import { useApi } from "@/hooks/use-api";
import { useAuth } from "@/hooks/use-auth";
import { useTranslation } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import {
  formatCompactNumberID,
  formatCostIDR,
  formatCostUSD,
  formatMilliseconds,
  formatNumberID,
} from "@/lib/format";
import { Alert } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Section } from "@/components/shared/section";
import { WindowSelector } from "./window-selector";

interface LatencyTileProps {
  label: string;
  percentile: keyof LatencyPercentiles | "count";
  data: LatencyPercentiles | null;
}

function LatencyTile({ label, percentile, data }: LatencyTileProps) {
  const value = data ? data[percentile] : null;
  const formatted =
    value === null || value === undefined
      ? "—"
      : percentile === "count"
        ? formatCompactNumberID(Number(value))
        : formatMilliseconds(Number(value));
  return (
    <div className="rounded-lg border border-ocean-100 bg-white p-4">
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1 text-2xl font-medium text-ocean-900">{formatted}</div>
    </div>
  );
}

export function InferenceTab() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [window, setWindow] = useState<ObservabilityWindow>("24h");

  const latencyFetcher = useCallback(
    () =>
      ObservabilityService.getInferenceLatency({
        window,
        workspace_id: user?.workspace_id ?? undefined,
      }),
    [window, user?.workspace_id]
  );
  const topModelsFetcher = useCallback(
    () =>
      ObservabilityService.getTopModels({
        window,
        workspace_id: user?.workspace_id ?? undefined,
        limit: 10,
      }),
    [window, user?.workspace_id]
  );
  const costFetcher = useCallback(
    () =>
      ObservabilityService.getCost({
        window,
        workspace_id: user?.workspace_id ?? undefined,
      }),
    [window, user?.workspace_id]
  );

  const latency = useApi(latencyFetcher, { deps: [window, user?.workspace_id] });
  const topModels = useApi(topModelsFetcher, {
    deps: [window, user?.workspace_id],
  });
  const cost = useApi(costFetcher, { deps: [window, user?.workspace_id] });

  const overall = latency.data?.overall ?? null;
  const stages = latency.data?.by_stage ?? {};

  // Sesi FIRST-TOKEN-LATENCY (v0.37.1) — group the stages into a
  // legible "Gateway (governance) vs Provider" story. Gateway stages
  // are Monago overhead (tens of ms). Provider stages split into
  // first_token (responsiveness) + streaming (length-sensitive) +
  // total. Anything we don't know how to bucket falls into "Other"
  // so a future stage rolls in without code changes.
  const GATEWAY_STAGES = ["policy_check", "pii_scan", "security_scan"] as const;
  const PROVIDER_STAGES = [
    "provider_first_token",
    "provider_streaming",
    "provider_call",
  ] as const;
  const gatewayEntries = GATEWAY_STAGES.flatMap((name) =>
    name in stages ? [[name, stages[name]] as const] : []
  );
  const providerEntries = PROVIDER_STAGES.flatMap((name) =>
    name in stages ? [[name, stages[name]] as const] : []
  );
  const knownSet = new Set<string>([...GATEWAY_STAGES, ...PROVIDER_STAGES]);
  const otherEntries = Object.entries(stages).filter(
    ([name]) => !knownSet.has(name)
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 tablet:flex-row tablet:items-center tablet:justify-between">
        <p className="text-sm text-neutral-600">
          {t("observability.inference.intro")}
        </p>
        <WindowSelector value={window} onChange={setWindow} />
      </div>

      {latency.error && (
        <Alert variant="warning">{parseApiError(latency.error)}</Alert>
      )}

      <div className="grid grid-cols-2 gap-3 tablet:grid-cols-4">
        <LatencyTile
          label={t("observability.inference.tileP50")}
          percentile="p50_ms"
          data={overall}
        />
        <LatencyTile
          label={t("observability.inference.tileP95")}
          percentile="p95_ms"
          data={overall}
        />
        <LatencyTile
          label={t("observability.inference.tileP99")}
          percentile="p99_ms"
          data={overall}
        />
        <LatencyTile
          label={t("observability.inference.tileTotal")}
          percentile="count"
          data={overall}
        />
      </div>

      <Section
        title={t("observability.inference.stagesTitle")}
        description={t("observability.inference.stagesDescription")}
      >
        {latency.isLoading && !latency.data ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("observability.inference.stagesColStage")}
                  </TableHead>
                  <TableHead>
                    {t("observability.inference.stagesColP50")}
                  </TableHead>
                  <TableHead>
                    {t("observability.inference.stagesColP95")}
                  </TableHead>
                  <TableHead>
                    {t("observability.inference.stagesColP99")}
                  </TableHead>
                  <TableHead>
                    {t("observability.inference.stagesColCount")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.keys(stages).length === 0 ? (
                  <TableEmpty
                    colSpan={5}
                    message={t("observability.inference.stagesEmpty")}
                  />
                ) : (
                  <>
                    {gatewayEntries.length > 0 && (
                      <>
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="bg-ocean-50/60 text-xs font-medium uppercase tracking-wide text-ocean-700"
                          >
                            {t("observability.inference.stagesGroupGateway")}
                          </TableCell>
                        </TableRow>
                        {gatewayEntries.map(([name, stats]) => (
                          <TableRow key={name}>
                            <TableCell className="font-medium text-ocean-900">
                              {name}
                            </TableCell>
                            <TableCell>
                              {formatMilliseconds(stats.p50_ms)}
                            </TableCell>
                            <TableCell>
                              {formatMilliseconds(stats.p95_ms)}
                            </TableCell>
                            <TableCell>
                              {formatMilliseconds(stats.p99_ms)}
                            </TableCell>
                            <TableCell>
                              {formatCompactNumberID(stats.count)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                    {providerEntries.length > 0 && (
                      <>
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="bg-ocean-50/60 text-xs font-medium uppercase tracking-wide text-ocean-700"
                          >
                            {t("observability.inference.stagesGroupProvider")}
                          </TableCell>
                        </TableRow>
                        {providerEntries.map(([name, stats]) => (
                          <TableRow key={name}>
                            <TableCell className="font-medium text-ocean-900">
                              {name}
                            </TableCell>
                            <TableCell>
                              {formatMilliseconds(stats.p50_ms)}
                            </TableCell>
                            <TableCell>
                              {formatMilliseconds(stats.p95_ms)}
                            </TableCell>
                            <TableCell>
                              {formatMilliseconds(stats.p99_ms)}
                            </TableCell>
                            <TableCell>
                              {formatCompactNumberID(stats.count)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                    {otherEntries.length > 0 && (
                      <>
                        <TableRow>
                          <TableCell
                            colSpan={5}
                            className="bg-neutral-50 text-xs font-medium uppercase tracking-wide text-neutral-600"
                          >
                            {t("observability.inference.stagesGroupOther")}
                          </TableCell>
                        </TableRow>
                        {otherEntries.map(([name, stats]) => (
                          <TableRow key={name}>
                            <TableCell className="font-medium text-ocean-900">
                              {name}
                            </TableCell>
                            <TableCell>
                              {formatMilliseconds(stats.p50_ms)}
                            </TableCell>
                            <TableCell>
                              {formatMilliseconds(stats.p95_ms)}
                            </TableCell>
                            <TableCell>
                              {formatMilliseconds(stats.p99_ms)}
                            </TableCell>
                            <TableCell>
                              {formatCompactNumberID(stats.count)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>

      <Section
        title={t("observability.inference.costTitle")}
        description={t("observability.inference.costDescription")}
      >
        {cost.error ? (
          <Alert variant="warning">{parseApiError(cost.error)}</Alert>
        ) : cost.isLoading && !cost.data ? (
          <Skeleton className="h-28 w-full" />
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 tablet:grid-cols-3">
              <div className="rounded-lg border border-ocean-100 bg-white p-4">
                <div className="label-eyebrow">
                  {t("observability.inference.costTileTotalUsd")}
                </div>
                <div className="mt-1 text-2xl font-medium text-ocean-900">
                  {formatCostUSD(cost.data?.total_estimated_cost_usd)}
                </div>
              </div>
              <div className="rounded-lg border border-ocean-100 bg-white p-4">
                <div className="label-eyebrow">
                  {t("observability.inference.costTileTotalIdr")}
                </div>
                <div className="mt-1 text-2xl font-medium text-ocean-900">
                  {formatCostIDR(cost.data?.total_estimated_cost_idr)}
                </div>
                {cost.data?.usd_to_idr_rate ? (
                  <div className="mt-1 text-xs text-neutral-600">
                    {t("observability.inference.costFxCaption", {
                      rate: formatNumberID(
                        Math.round(Number(cost.data.usd_to_idr_rate))
                      ),
                    })}
                  </div>
                ) : null}
              </div>
              <div className="rounded-lg border border-ocean-100 bg-white p-4">
                <div className="label-eyebrow">
                  {t("observability.inference.costPricingSourceLabel")}
                </div>
                <div className="mt-1 flex items-center gap-2 text-sm font-medium text-ocean-900">
                  {cost.data?.pricing_source ?? "—"}
                  {cost.data?.pricing_source === "default_fallback" ? (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                      {t("observability.inference.costPricingFallbackBadge")}
                    </span>
                  ) : null}
                </div>
                {cost.data?.pricing_version ? (
                  <div className="mt-1 text-xs text-neutral-600">
                    {cost.data.pricing_version}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        )}
      </Section>

      <Section
        title={t("observability.inference.topModelsTitle")}
        description={t("observability.inference.topModelsDescription")}
      >
        {topModels.error ? (
          <Alert variant="warning">{parseApiError(topModels.error)}</Alert>
        ) : topModels.isLoading && !topModels.data ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <div className="overflow-hidden rounded-lg border border-ocean-100 bg-white">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("observability.inference.topModelsColModel")}
                  </TableHead>
                  <TableHead>
                    {t("observability.inference.topModelsColRequests")}
                  </TableHead>
                  <TableHead>
                    {t("observability.inference.topModelsColTokens")}
                  </TableHead>
                  <TableHead>
                    {t("observability.inference.topModelsColCostUsd")}
                  </TableHead>
                  <TableHead>
                    {t("observability.inference.topModelsColCostIdr")}
                  </TableHead>
                  <TableHead>
                    {t("observability.inference.topModelsColAvgLatency")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!topModels.data || topModels.data.items.length === 0 ? (
                  <TableEmpty
                    colSpan={6}
                    message={t("observability.inference.topModelsEmpty")}
                  />
                ) : (
                  topModels.data.items.map((item) => (
                    <TableRow key={item.model}>
                      <TableCell className="font-medium text-ocean-900">
                        {item.model}
                      </TableCell>
                      <TableCell>
                        {formatCompactNumberID(item.request_count)}
                      </TableCell>
                      <TableCell>
                        {formatCompactNumberID(item.total_tokens)}
                      </TableCell>
                      <TableCell>
                        {formatCostUSD(item.estimated_cost_usd)}
                      </TableCell>
                      <TableCell>
                        {formatCostIDR(item.estimated_cost_idr)}
                      </TableCell>
                      <TableCell>
                        {formatMilliseconds(item.avg_latency_ms)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </Section>
    </div>
  );
}
